import { ApolloProvider, useQuery } from '@apollo/client'
import { Suspense, lazy, useEffect } from 'react'
import { Navigate, Outlet, matchPath, useLocation, useNavigate, useParams, useRoutes } from 'react-router-dom'

import {
  CharityActivity,
  CharityPortalDirectDeposit,
  CharityPortalInviteUsers,
  CharityPortalProfile,
  CharityPortalProjects,
  Disbursements,
  Gifts,
  GiveGeniusResources,
  RecurringGifts,
} from './pages/charity-portal'
import InstanceBrandingInjector from './components/InstanceBrandingInjector'
import {
  NavigationGuardProvider,
  getBrandedTitle,
  useAdvisorAccess,
  useAuth,
  useBranding,
  usePermissions,
} from './hooks'
import { LocalStorage, SHARE_FUND_CODE } from './components/local-storage/localStorage'
import {
  Advisor,
  BrandedFooter,
  Charity as CharityC,
  ConfirmProvider,
  Donor,
  ErrorBoundary,
  GlobalAlertProvider,
  Header,
  LinearProgress,
  Navbar,
  NoticeHeader,
  PendingInviteNotifier,
} from './components'
import { client as clientConfig, queries } from './graphql'
import { MaintenanceGuard } from './hooks/useMaintenance'
import { captureExternalReferrer, captureUtmParameters } from './utils'
import { detectActiveContext } from './components/context-switcher/compute-contexts'
import {
  Charity,
  CharityDetailsConfirm,
  CharityLanding,
  CharityPaylink,
  CharityPortalDashboard,
  CharityRedirect,
  ClaimCharity,
  ClaimCharityByUserId,
  ClaimFund,
  ClaimFundSuccess,
  ConfirmAddedEmail,
  ConfirmEmail,
  EmailFlowOptOut,
  Documents,
  ForgotPassword,
  FundList,
  GenerosityWorksRedirect,
  LandingPage,
  Login,
  Logout,
  MyGivingFund,
  NoMatch,
  Profile,
  ResetPassword,
  SignUp,
  SignUpSuccess,
  Successor,
} from './pages'
import {
  AcceptConnection,
  AcceptConnectionById,
  AdvisorDashboard,
  AdvisorSignUp,
  CreateClientFund,
} from './pages/advisor'
import {
  ActivityIndexRedirect,
  BankTransferZum,
  Cheque,
  Contribute,
  ContributeRedirect,
  CreditCard,
  Cryptocurrency,
  DAF,
  ETransfer,
  Fund,
  FundActivity,
  FundIndex,
  GivingWallet,
  InitiatedContributions,
  InvestmentAccount,
  InvestmentPortfolioActivity,
  InvestmentUnlinkedActivity,
  OtherAssets,
  PayButton,
  ReceiveADafTransfer,
  Recurrent,
  RedirectToDefaultFund,
  Securities,
  ShareFundsWithGenerosityFund,
  ShareFundsWithOthers,
} from './pages/funds'
import { Charities, SearchByLocation, SearchByText } from './pages/charities'
import './App.css'
import SplashScreen from './mobile/SplashScreen'
import ChatApp from './ai/ai-chat-app/app'
import { GiftStepper } from './pages/gift'
import { PaymentMethodsLayout } from './pages/profile/payment-methods-layout'
import { ManageGivingFundDeletion } from './pages/profile/manage-giving-fund-deletion'
import { SecuritySettingsLayout } from './pages/profile/security-settings-layout'
import { CustodianSettings } from './pages/profile/custodian-settings'
import { AdvisorManagement } from './pages/profile/advisor-management'
import { UserDetailsForm } from './pages/profile/user-details-form'
import { DonorDetailsForm } from './pages/profile/donor-details-form'
import { DeleteUserTab } from './pages/profile/delete-user-tab'
import { AdvisorPersonalDetailsForm } from './pages/profile/advisor-personal-details-form'
import { RepresentativeDetailsForm } from './pages/profile/representative-details-form'
import { DeletePortalAccessTab } from './pages/profile/delete-portal-access-tab'
import { DonorProfileRedirect } from './pages/profile/donor-profile-redirect'

const Management = lazy(() => import('./management'))
const PresenterStandaloneView = lazy(() => import('./presenter-standalone-view'))
const PresenterPublicView = lazy(() => import('./presenter-public-view'))

function ScrollToTop({ pathname }) {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])
  return null
}

function BrandedTitle() {
  const branding = useBranding()

  useEffect(() => {
    document.title = getBrandedTitle(branding?.platformName)
  }, [branding?.platformName])

  return null
}

function MyCharity() {
  const { data: { myCharity } = {} } = useQuery(queries.charities.myCharity)

  if (myCharity) {
    return <Navigate to={`${myCharity.id}`} />
  }
  return <LinearProgress />
}

function CharityConfirm() {
  const { data: { myCharity } = {} } = useQuery(queries.charities.myCharity)

  if (myCharity) {
    return <Navigate to={`/charities/${myCharity.id}/confirm`} />
  }
  return <LinearProgress />
}

function App() {
  const routes = [
    {
      element: <Private />,
      children: [
        { index: true, element: <Navigate to="/funds" replace /> },
        { path: '/advisor/create-fund', element: <CreateClientFund /> },
        {
          path: '/funds',
          element: <Outlet />,
          children: [
            { index: true, element: <FundIndex /> },
            {
              path: 'default/share-funds-with-generosity-fund/:generosityFundId',
              exact: true,
              element: (
                <RedirectToDefaultFund
                  using={({ generosityFundId }) => `share-funds-with-generosity-fund/${generosityFundId}`}
                />
              ),
            },
            {
              path: 'default/grant/:charityId',
              exact: true,
              element: <RedirectToDefaultFund using={({ charityId }) => `grant/${charityId}`} />,
            },
            {
              path: ':fundId',
              element: <Fund />,
              children: [
                { index: true, element: <MyGivingFund /> },
                { path: 'share-funds-with-others', exact: true, element: <ShareFundsWithOthers /> },
                {
                  path: 'share-funds-with-generosity-fund/:generosityFundId',
                  exact: true,
                  element: <ShareFundsWithGenerosityFund />,
                },
                {
                  path: 'grant',
                  element: <Charities />,
                  children: [
                    { index: true, element: <Navigate to="search-by-text" /> },
                    { path: 'search-by-text', element: <SearchByText /> },
                    { path: 'search-by-location', element: <SearchByLocation /> },
                  ],
                },
                { path: 'grant/:charityId', element: <CharityRedirect fallback={CharityLanding} /> },
                { path: 'grant/:charityId/landing', element: <LandingPage /> },
                { path: 'grant/:charityId/give', element: <Charity /> },
                { path: 'documents', exact: true, element: <Documents /> },
                { path: 'fund-stats', exact: true, element: <Navigate to=".." /> },
                {
                  path: 'activity',
                  element: <FundActivity />,
                  children: [
                    { index: true, element: <ActivityIndexRedirect /> },
                    { path: 'giving-wallet', element: <GivingWallet /> },
                    {
                      path: 'investment-account',
                      children: [
                        { index: true, element: <InvestmentAccount /> },
                        { path: 'other', element: <InvestmentUnlinkedActivity /> },
                        { path: ':portfolioId', element: <InvestmentPortfolioActivity /> },
                      ],
                    },
                    {
                      path: 'recurring',
                      element: <Recurrent />,
                    },
                  ],
                },
                {
                  path: 'contribute',
                  element: <Contribute />,
                  children: [
                    { index: true, element: <ContributeRedirect /> },
                    { path: 'credit-card', element: <CreditCard /> },
                    { path: 'e-transfer', element: <ETransfer /> },
                    { path: 'cheque', element: <Cheque /> },
                    { path: 'daf-transfer', element: <DAF /> },
                    { path: 'bank-transfer', element: <BankTransferZum /> },
                    { path: 'securities', element: <Securities /> },
                    { path: 'initiated-contributions', element: <InitiatedContributions /> },
                    { path: 'cryptocurrency', element: <Cryptocurrency /> },
                    { path: 'apple-pay', element: <PayButton /> },
                    { path: 'other-assets', element: <OtherAssets /> },
                  ],
                },
              ],
            },
          ],
        },
        {
          path: '/receive-a-daf-transfer',
          element: <Outlet />,
          children: [
            { index: true, exact: true, element: <NoMatch /> },
            {
              path: ':id',
              element: <Outlet />,
              children: [
                { index: true, exact: true, element: <NoMatch /> },
                { path: ':receiveToken', element: <ReceiveADafTransfer /> },
              ],
            },
          ],
        },
        {
          path: '/advisor/my-profile',
          element: <Profile />,
          children: [
            { index: true, exact: true, element: <Navigate to="user-details" /> },
            { path: 'user-details', element: <UserDetailsForm /> },
            { path: 'personal-details', element: <AdvisorPersonalDetailsForm /> },
            { path: 'security', element: <SecuritySettingsLayout /> },
            { path: 'custodian-settings', element: <CustodianSettings /> },
            {
              path: 'delete',
              element: (
                <>
                  <DeletePortalAccessTab context="advisor" />
                  <DeleteUserTab />
                </>
              ),
            },
          ],
        },
        {
          path: '/profile/:fundId',
          element: <Profile />,
          children: [
            { index: true, element: <Navigate to="user-details" replace /> },
            { path: 'user-details', element: <UserDetailsForm /> },
            { path: 'security', element: <SecuritySettingsLayout /> },
            { path: 'donor-details', element: <DonorDetailsForm /> },
            { path: 'payment-methods', element: <PaymentMethodsLayout /> },
            { path: 'advisor-management', element: <AdvisorManagement /> },
            { path: 'successor-requests', element: <Successor /> },
            {
              path: 'delete',
              element: (
                <>
                  <ManageGivingFundDeletion />
                  <DeleteUserTab />
                </>
              ),
            },
            { path: 'personal-details', element: <Navigate to="../user-details" replace /> },
          ],
        },
        { path: '/profile', element: <DonorProfileRedirect /> },
        { path: '/profile/*', element: <DonorProfileRedirect /> },
      ],
    },
    {
      element: <CharityPortal />,
      children: [
        { index: true, element: <Navigate to="/charities" /> },
        {
          path: '/charities',
          element: <Outlet />,
          children: [
            { index: true, element: <MyCharity /> },
            {
              path: 'confirm',
              element: <CharityConfirm />,
            },
            {
              path: ':id',
              element: <Outlet />,
              children: [
                { index: true, element: <Navigate to="dashboard" /> },
                { path: 'confirm', element: <CharityDetailsConfirm /> },
                { path: 'dashboard', element: <CharityPortalDashboard /> },
                {
                  path: 'activity',
                  element: <CharityActivity />,
                  children: [
                    { index: true, element: <Navigate to="gifts" /> },
                    { path: 'gifts', element: <Gifts /> },
                    { path: 'disbursements', element: <Disbursements /> },
                    { path: 'recurring-gifts', element: <RecurringGifts /> },
                  ],
                },
                { path: 'direct-deposit', element: <CharityPortalDirectDeposit /> },
                { path: 'designations', element: <CharityPortalProjects /> },
                { path: 'charity-profile', element: <CharityPortalProfile /> },
                {
                  path: 'my-profile',
                  element: <Profile />,
                  children: [
                    { index: true, exact: true, element: <Navigate to="user-details" /> },
                    { path: 'user-details', element: <UserDetailsForm /> },
                    { path: 'security', element: <SecuritySettingsLayout /> },
                    // Charity-portal-specific tabs
                    { path: 'representative-details', element: <RepresentativeDetailsForm /> },
                    {
                      path: 'delete',
                      element: (
                        <>
                          <DeletePortalAccessTab context="charity" />
                          <DeleteUserTab />
                        </>
                      ),
                    },
                  ],
                },
                { path: 'invite-users', element: <CharityPortalInviteUsers /> },
                { path: 'givegenius', element: <GiveGeniusResources /> },
                // { path: 'claim/:token', element: <ClaimCharity /> },
              ],
            },
          ],
        },
        { path: '/charities/:id/confirm', exact: true, element: <CharityDetailsConfirm /> },
        // { path: '/charities/:id/gifts', exact: true, element: <Gifts /> },
      ],
    },
    {
      element: <Public />,
      children: [
        { path: '/login', exact: true, element: <Login /> },
        { path: '/logout', exact: true, element: <Logout /> },
        { path: '/sign-up/advisor/:token', exact: true, element: <AdvisorSignUp /> },
        {
          path: '/sign-up',
          element: <Outlet />,
          children: [
            { index: true, element: <Navigate to="signup" /> },
            { path: 'signup', element: <SignUp /> },
            { path: 'success', element: <SignUpSuccess /> },
          ],
        },
        { path: '/signup', exact: true, element: <Navigate to="/sign-up" /> },
        { path: '/confirm/:userId/:confirmationToken', element: <ConfirmEmail /> },
        { path: '/confirm-new-email/:confirmationToken', element: <ConfirmAddedEmail /> },
        { path: '/forgot-password', exact: true, element: <ForgotPassword /> },
        { path: '/reset-password/:token', element: <ResetPassword /> },
        { path: '/gift/charity/:charityId', element: <CharityRedirect /> },
        { path: '/gift/genfund/:fundId', element: <GiftStepper /> },
        { path: '/charities/:id/paylink/:token', exact: true, element: <CharityPaylink /> },
        { path: '/charities/:id/claim/:token', element: <ClaimCharity /> },
        { path: '/charities/:id/claim/:userId/:token', element: <ClaimCharityByUserId /> },
        // { path: '/charities/:id/claim', exact: true, element: <ClaimCharity /> },
        { path: '/give/:fundId', exact: true, element: <GenerosityWorksRedirect /> },
        { path: '/generosity-works-redirect', exact: true, element: <GenerosityWorksRedirect /> },
        { path: '/claim-fund/:token', exact: true, element: <ClaimFund /> },
        { path: '/claim-fund/:token/success', exact: true, element: <ClaimFundSuccess /> },
        { path: '/advisor/accept/:token', exact: true, element: <AcceptConnection /> },
        { path: '/advisor/accept-by-id/:flowId', exact: true, element: <AcceptConnectionById /> },
        // FLIGHT-850: the footer link in an automated flow email. Unauthenticated
        // on purpose - stopping unwanted email must not require a login.
        { path: '/email-flow-opt-out/:token', exact: true, element: <EmailFlowOptOut /> },
        { path: '*', element: <NoMatch /> },
      ],
    },
    {
      // No wrapper -- bare <Outlet/>, unauthenticated. Same reasoning as
      // /presenter/view/:id's isStandaloneView carve-out in PrivateAdmin:
      // the fullscreen PresentationViewer owns the whole viewport, so this
      // route skips <Public/>'s <Main>/<BrandedFooter> chrome entirely
      // rather than fighting it with more pathname checks.
      path: '/presenter/public/:token',
      element: (
        <ErrorBoundary>
          <Suspense fallback={<LinearProgress />}>
            <PresenterPublicView />
          </Suspense>
        </ErrorBoundary>
      ),
    },
    {
      element: <PrivateAdmin />,
      children: [
        {
          path: '/management/*',
          element: (
            <ErrorBoundary>
              <Suspense fallback={<LinearProgress />}>
                <Management />
              </Suspense>
            </ErrorBoundary>
          ),
        },
        {
          path: '/experimental/landing-page/:fundId/:charityId',
          element: (
            <ErrorBoundary>
              <Suspense fallback={<LinearProgress />}>
                <br />
                <LandingPage />
              </Suspense>
            </ErrorBoundary>
          ),
        },
        { path: '/ai/chat', element: <ChatApp /> },
        {
          path: '/presenter/view/:id',
          element: (
            <ErrorBoundary>
              <Suspense fallback={<LinearProgress />}>
                <PresenterStandaloneView />
              </Suspense>
            </ErrorBoundary>
          ),
        },
      ],
    },
  ]

  const body = useRoutes(routes)
  const { pathname } = useLocation()
  const hideHeader =
    pathname.startsWith('/gift/charity/') ||
    pathname.startsWith('/gift/genfund/') ||
    pathname.startsWith('/presenter/view/') ||
    pathname.startsWith('/presenter/public/')
  return (
    <MaintenanceGuard>
      <ApolloProvider client={clientConfig}>
        <GlobalAlertProvider>
          <LocalStorage>
            <ConfirmProvider>
              <NavigationGuardProvider>
                <ScrollToTop pathname={pathname} />
                <BrandedTitle />
                <InstanceBrandingInjector />
                {!hideHeader && <Header />}
                <SplashScreen />
                <PendingInviteNotifier />
                {body}
              </NavigationGuardProvider>
            </ConfirmProvider>
          </LocalStorage>
        </GlobalAlertProvider>
      </ApolloProvider>
    </MaintenanceGuard>
  )
}

export default App

function Public() {
  const { pathname } = useLocation()
  // GiveGenius pages (/gift/charity/:id and /gift/genfund/:id) have their own footer
  const hideFooter = pathname.startsWith('/gift/')

  return (
    <>
      <Main>
        <Outlet />
      </Main>
      {!hideFooter && <BrandedFooter />}
    </>
  )
}

function Private() {
  const { data: { me } = {}, loading } = useQuery(queries.user.me)
  const address = me?.funds?.find((f) => !f.name?.startsWith('charity-') && !f.closed)?.fundProfile?.address
  const isCharityUser = (me?.charityUsers || []).length > 0
  const hasRealDonorFunds = (me?.funds || []).some((f) => !f.name?.startsWith('charity-') && !f.closed)
  const hasOpenAdvisorPortal = (me?.fundProfiles || []).some((p) => p.fundType === 'advisor' && !p.closed)
  const hasOpenAdminPortal = (me?.fundProfiles || []).some((p) => p.fundType === 'admin' && !p.closed)
  const navigate = useNavigate()
  const [{ isLoggedIn, isAdmin }] = useAuth()
  const { pathname, state } = useLocation()
  const routeParams = useParams()
  const { afterLogin, _impersonationTransition } = state ?? {}
  const { isAdvisor, loading: advisorLoading } = useAdvisorAccess()
  // isPureCharityUser uses isAdvisor (not hasOpenAdvisorPortal) so that a pending advisor invite
  // does not suppress the charity redirect. An advisor FundProfile is created at invite time, but
  // RBAC access (reflected in isAdvisor) is only granted on acceptance.
  const isPureCharityUser = isCharityUser && !hasRealDonorFunds && !isAdvisor

  // Check if we're on a dashboard index page (where widgets handle the header)
  const { fundId } = useParams()
  const isDonorDashboard = fundId && pathname === `/funds/${fundId}`
  const activeContext = detectActiveContext(pathname)
  const showDonorHeader = activeContext?.kind === 'fund' && !isDonorDashboard
  const { loading: _loadingMyFundActivity, data: myFundActivityData } = useQuery(queries.funds.myFundActivity, {
    variables: { fundId: Number(fundId) },
  })
  const dafTransfers = myFundActivityData?.me?.fund?.dafTransfers || []

  const pendingShareFunds = dafTransfers.filter((dt) => {
    const isStateIssued = dt.state && dt.state === 'issued'
    const isReceivingFund = dt.receivingFundId && dt.receivingFundId === fundId
    const isReceivingUser = dt.receivingUserId && dt.receivingUserId === me.id
    const isSentTo = me && dt.sentTo && dt.sentTo === me.email
    return isStateIssued && (isReceivingFund || isReceivingUser || isSentTo)
  })

  if (pendingShareFunds.length === 0) {
    localStorage.removeItem(SHARE_FUND_CODE)
  }

  pendingShareFunds.forEach((sf) => {
    const expireTime = Date.now() + 1000 * 3600 * 24 * 30 // 30 days
    const expires = new Date()
    expires.setTime(expireTime)
    const expireString = expires.toUTCString()

    localStorage.setItem(
      SHARE_FUND_CODE,
      JSON.stringify({
        id: sf.id,
        code: sf.receiveToken,
        expires: expireString,
      })
    )
  })

  const isCharityPortalRoute = () =>
    pathname.indexOf('/charities') > -1 && (pathname.indexOf('/paylink/') < 0 || pathname.indexOf('/claim/'))

  useEffect(() => {
    if (!isLoggedIn) navigate('/login', { state: { afterLogin: afterLogin ?? pathname } })

    if (isPureCharityUser && !advisorLoading) {
      navigate('/charities', { replace: true })
      return
    }

    if (
      !_impersonationTransition &&
      me &&
      !hasRealDonorFunds &&
      !isCharityUser &&
      !hasOpenAdvisorPortal &&
      (isAdmin || hasOpenAdminPortal)
    ) {
      navigate('/management', { replace: true })
      return
    }

    if (!isCharityUser && isCharityPortalRoute()) {
      navigate('/404') // Only charity portal users should have access to it
    } else if (
      isCharityPortalRoute() &&
      'id' in routeParams &&
      !me?.charityUsers?.find((cu) => cu.charityId === routeParams.id)
    ) {
      navigate('/404')
    }

    if (me && !isCharityUser && hasRealDonorFunds && !address && !pathname.startsWith('/profile')) {
      const firstFundId = me.funds?.find((f) => !f.name?.startsWith('charity-') && !f.closed)?.id
      navigate(firstFundId ? `/profile/${firstFundId}` : '/profile', {
        state: { afterLogin: afterLogin ?? pathname },
        replace: true,
      })
    }
  }, [
    address,
    isCharityUser,
    isPureCharityUser,
    hasRealDonorFunds,
    hasOpenAdvisorPortal,
    isAdvisor,
    advisorLoading,
    hasOpenAdminPortal,
    isAdmin,
    _impersonationTransition,
    pathname,
    isLoggedIn,
    me,
    navigate,
    afterLogin,
    routeParams,
    isCharityPortalRoute,
  ])

  // Detect Daf transfer token and store to local storage.
  useEffect(() => {
    const dafURLPattern = /.*\/receive-a-daf-transfer\/([0-9]+)\/(.+)/
    const giftCodeUrl = matchPath('/receive-a-daf-transfer/:id/:token', pathname) ? pathname : ''
    const match = giftCodeUrl?.match(dafURLPattern)
    if (!match) return // Guard clause if not valid URL

    // Get expiration Date
    const expireTime = Date.now() + 1000 * 3600 * 24 * 30 // 30 days
    const expires = new Date()
    expires.setTime(expireTime)
    const expireString = expires.toUTCString()

    localStorage.setItem(
      SHARE_FUND_CODE,
      JSON.stringify({
        id: match[1],
        code: match[2],
        expires: expireString,
      })
    )
  }, [isLoggedIn, pathname, navigate, afterLogin])

  if (loading || advisorLoading) return <LinearProgress />
  return (
    <>
      <Advisor.ImpersonationBanner />
      <Navbar />
      <NoticeHeader />
      {showDonorHeader && <Donor.Header />}
      <Main>
        <Outlet />
      </Main>
      <BrandedFooter />
    </>
  )
}

function CharityPortal() {
  const navigate = useNavigate()
  const [{ isLoggedIn, isAdmin }] = useAuth()
  const routeParams = useParams()
  const { data: { me } = {}, loading } = useQuery(queries.user.me)

  useEffect(() => {
    // FLIGHT-570 / FLIGHT-576: allow access if the user has a CharityUser link
    // to this charity, even when their profile.userType is not 'charity'.
    // Multi-portal users (Nancy: donor + advisor + charity) have userType='donor'
    // but still belong to one or more charities and must be allowed into the
    // charity portal when they switch into a charity context. Same "active
    // portal beats user-level flag" pattern fixed in navbar.jsx.
    const targetCharityId = routeParams?.id
    const hasMatchingCharityUser = targetCharityId && me?.charityUsers?.some((cu) => cu.charityId === targetCharityId)
    // A reassigned charity portal has a charity-<id> fund but may have no CharityUser record
    const hasMatchingCharityFund =
      targetCharityId && (me?.funds || []).some((f) => f.name === `charity-${targetCharityId}` && !f.closed)
    const isCharityUser = (me?.charityUsers || []).length > 0 || hasMatchingCharityUser || hasMatchingCharityFund

    if (!isLoggedIn) navigate('/login')
    if (loading) return
    if (!isCharityUser) {
      // Not a charity user - redirect to appropriate portal instead of 404
      // This prevents a 404 flash during impersonation transitions
      navigate(isAdmin ? '/management' : '/', { replace: true })
      return
    }
    // Check if this specific charity's portal is closed (per-charity fund check)
    if (targetCharityId) {
      const targetCharityFund = (me?.funds || []).find((f) => f.name === `charity-${targetCharityId}`)
      if (targetCharityFund?.closed) {
        navigate('/', { replace: true })
      }
    }
  }, [isLoggedIn, isAdmin, navigate, me, loading, routeParams])

  if (loading) {
    return ''
  }

  return (
    <>
      <Advisor.ImpersonationBanner />
      <Navbar />
      <NoticeHeader />
      <CharityC.Header />
      <Main>
        <Outlet />
      </Main>
      <BrandedFooter />
    </>
  )
}

function PrivateAdmin() {
  const { data: { me } = {}, loading } = useQuery(queries.user.me)
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const isStandaloneView = pathname.startsWith('/presenter/view/')
  const [{ isLoggedIn, isAdmin }] = useAuth()
  // Check RBAC permissions for management portal access
  const { hasPermission: hasManageAccess, loading: permissionsLoading } = usePermissions([
    'manage:access',
    'system:admin',
  ])

  useEffect(() => {
    // Wait for all data to load before checking access
    if (loading || permissionsLoading) return

    if (!isLoggedIn) {
      navigate('/login')
      return
    }

    // Pure charity users (no real donor funds) can't access admin — redirect to charity portal.
    // Multi-portal users who also have admin access must be allowed through.
    const meHasRealFunds = (me?.funds || []).some((f) => !f.name?.startsWith('charity-'))
    const isPureCharity = (me?.charityUsers || []).length > 0 && !meHasRealFunds
    if (isPureCharity && !isAdmin && !hasManageAccess) {
      navigate('/charities', { replace: true })
      return
    }

    // Allow access if user has legacy admin flag OR RBAC manage:access/system:admin permission
    if (!isAdmin && !hasManageAccess) {
      navigate('/', { replace: true })
      return
    }

    // Block access if the admin portal FundProfile is closed
    const adminPortalClosed = (me?.fundProfiles || []).some((p) => p.fundType === 'admin' && p.closed)
    const adminPortalOpen = (me?.fundProfiles || []).some((p) => p.fundType === 'admin' && !p.closed)
    if (!adminPortalOpen && adminPortalClosed) {
      navigate('/', { replace: true })
    }
  }, [isLoggedIn, isAdmin, hasManageAccess, me?.charityUsers, navigate, loading, permissionsLoading])

  if (loading || permissionsLoading) return <LinearProgress />

  return (
    <>
      {!isStandaloneView && (
        <>
          <Advisor.ImpersonationBanner />
          <Navbar />
        </>
      )}
      <Outlet />
    </>
  )
}

function Main({ children }) {
  // Capture external referrer and UTM parameters on first page load for first-touch attribution
  useEffect(() => {
    captureExternalReferrer()
    captureUtmParameters()
  }, [])

  return (
    <main id="main-content" className="main">
      {children}
    </main>
  )
}
